// Diagnostics utility to help identify root causes of chunk loading failures
export class ChunkLoadDiagnostics {
  static collectDiagnosticInfo() {
    const diagnostics = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      connection: this.getConnectionInfo(),
      browser: this.getBrowserInfo(),
      cache: this.getCacheInfo(),
      errors: this.getRecentErrors(),
    }

    console.error('Chunk Load Diagnostics:', diagnostics)
    return diagnostics
  }

  private static getConnectionInfo() {
    const connection =
      (navigator as any).connection ||
      (navigator as any).mozConnection ||
      (navigator as any).webkitConnection

    return {
      effectiveType: connection?.effectiveType || 'unknown',
      downlink: connection?.downlink || 'unknown',
      rtt: connection?.rtt || 'unknown',
      saveData: connection?.saveData || false,
      online: navigator.onLine,
    }
  }

  private static getBrowserInfo() {
    return {
      cookieEnabled: navigator.cookieEnabled,
      doNotTrack: navigator.doNotTrack,
      language: navigator.language,
      platform: navigator.platform,
      vendor: navigator.vendor,
      hardwareConcurrency: navigator.hardwareConcurrency,
      memory: (performance as any)?.memory
        ? {
            usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
            totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
            jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit,
          }
        : 'unavailable',
    }
  }

  private static async getCacheInfo() {
    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys()
        const chunkCaches = cacheNames.filter(
          (name) =>
            name.includes('next-static') ||
            name.includes('chunks') ||
            name.includes('_next')
        )

        const cacheDetails = await Promise.all(
          chunkCaches.map(async (cacheName) => {
            const cache = await caches.open(cacheName)
            const keys = await cache.keys()
            return {
              name: cacheName,
              entryCount: keys.length,
              chunkFiles: keys
                .map((req) => req.url)
                .filter((url) => url.includes('chunks/')).length,
            }
          })
        )

        return {
          available: true,
          caches: cacheDetails,
          totalCaches: chunkCaches.length,
        }
      } catch (error) {
        return {
          available: true,
          error: error?.message,
        }
      }
    }

    return { available: false }
  }

  private static getRecentErrors() {
    // This would need to be integrated with your error tracking
    // For now, just check console errors
    const errors = []

    try {
      // Check for common chunk-related error patterns in recent console logs
      const originalConsoleError = console.error
      console.error = (...args) => {
        const message = args.join(' ')
        if (
          message.includes('Loading chunk') ||
          message.includes('ChunkLoadError') ||
          message.includes('Failed to fetch dynamically imported module')
        ) {
          errors.push({
            timestamp: new Date().toISOString(),
            message,
            stack: new Error().stack,
          })
        }
        originalConsoleError.apply(console, args)
      }
    } catch {
      // do nothing
    }

    return errors
  }
}
